import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ProgressBar } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Button from 'common/components/button/Button';
import { WizardFooterSlotContext } from 'common/components/wizardFooterSlot/WizardFooterSlot';
import PageContainer from 'common/components/pageContainer/PageContainer';
import PageHeader from 'common/components/pageHeader/PageHeader';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import ElementsService, { ImportSettingsDto, ImportStatus } from 'api/elements/ElementsService';
import FamiliesService from 'api/families/FamiliesService';
import { FamilyDto } from 'api/families/models/FamilyDto';
import { SelectInputOption } from 'common/components/selectInput/SelectInput';
import { ElementTypeDto } from 'api/elements/Models/ElementTypeDto';
import { UnitDto } from 'api/units/models/UnitDto';
import CatalogParametersService from 'api/catalogParameters/CatalogParametersService';
import { CatalogParameterDto } from 'api/catalogParameters/models/CatalogParameterDto';
import WorkTypesService from 'api/workTypes/WorkTypesService';
import UnitsService from 'api/units/UnitsService';
import Toast from 'common/services/Toast';
import StepUpload from './steps/StepUpload';
import StepMapping from './steps/StepMapping';
import StepPricePreview, { PricePreviewRowInfo } from './steps/StepPricePreview';
import { ExcelSheetData, ElementColumnMappingEntry, ElementImportRow, MappingFieldOption, FIXED_IMPORT_FIELDS, PriceClassification, PriceFormulaPreview } from './utils/types';
import { matchFamily, matchFamilyByName, cleanElementName } from './utils/familyMatcher';
import { buildImportPayload, buildPricePreviewRequest, cleanExcelNumber } from './utils/elementBuilder';
import styles from './ImportElementsScreen.module.scss';

interface SavedMapping {
    columnMappings: Record<string, string>; // excelColumnName -> elementField
}

function trimTrailingEmptyRows (rows: ElementImportRow[]): ElementImportRow[] {
    let lastNonEmpty = rows.length - 1;
    while (lastNonEmpty >= 0) {
        const row = rows[lastNonEmpty];
        const hasData = row.rawCells.some(cell => cell?.trim());
        if (hasData) break;
        lastNonEmpty--;
    }
    return rows.slice(0, lastNonEmpty + 1);
}

function ImportElementsScreen (): JSX.Element {
    const { t } = useTranslation();

    // Wizard state
    const [step, setStep] = useState(0);
    // Footer DOM node that step components portal their table pagination into.
    const [footerSlot, setFooterSlot] = useState<HTMLDivElement | null>(null);
    const [excelData, setExcelData] = useState<ExcelSheetData | null>(null);
    const [headerRowNumber, setHeaderRowNumber] = useState(4);
    const [columnMappings, setColumnMappings] = useState<ElementColumnMappingEntry[]>([]);
    const [importRows, setImportRows] = useState<ElementImportRow[]>([]);
    const [fileName, setFileName] = useState('');
    const [families, setFamilies] = useState<FamilyDto[]>([]);
    const [elementTypes, setElementTypes] = useState<SelectInputOption[]>([]);
    const [workTypes, setWorkTypes] = useState<SelectInputOption[]>([]);
    const [units, setUnits] = useState<SelectInputOption[]>([]);
    const familiesRef = useRef<FamilyDto[]>([]);
    const elementTypesRef = useRef<SelectInputOption[]>([]);
    const unitsRef = useRef<SelectInputOption[]>([]);

    // Catalog parameters for dynamic column mapping
    const [catalogParameters, setCatalogParameters] = useState<CatalogParameterDto[]>([]);

    // Import state
    const [jobId, setJobId] = useState<string | null>(null);
    const [status, setStatus] = useState<ImportStatus | null>(null);
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const pollingRef = useRef<NodeJS.Timeout | null>(null);

    // Price-formula preview state
    const [pricePreviews, setPricePreviews] = useState<Map<number, PriceFormulaPreview>>(new Map());
    const [priceOverrides, setPriceOverrides] = useState<Map<number, PriceClassification>>(new Map());
    const [priceAnalyzing, setPriceAnalyzing] = useState(false);

    // Build dynamic mapping fields from fixed fields + catalog parameters
    const mappingFields: MappingFieldOption[] = useMemo(() => {
        const paramFields: MappingFieldOption[] = catalogParameters
            .map(p => ({
                value: p.name,
                label: p.name,
            }));
        return [...FIXED_IMPORT_FIELDS, ...paramFields];
    }, [catalogParameters]);

    const mappingFieldsRef = useRef<MappingFieldOption[]>(mappingFields);
    mappingFieldsRef.current = mappingFields;

    // Load catalogs on mount
    const loadCatalogs = useCallback(async () => {
        try {
            const [familiesData, elementTypesData, workTypesData, unitsData, catalogParamsData] = await Promise.all([
                FamiliesService.getElementsCatalog(),
                ElementsService.getElementTypesCatalog(),
                WorkTypesService.getCatalog(),
                UnitsService.getFormattedCatalog(),
                CatalogParametersService.getCatalog(),
            ]);
            const elTypes = elementTypesData.map((x: ElementTypeDto) => ({ label: x.name, value: x.id }));
            const uList = unitsData.map((x: UnitDto) => ({ label: x.name, value: x.id }));
            setFamilies(familiesData);
            familiesRef.current = familiesData;
            setElementTypes(elTypes);
            elementTypesRef.current = elTypes;
            setWorkTypes(workTypesData);
            setUnits(uList);
            unitsRef.current = uList;
            setCatalogParameters(catalogParamsData);
        } catch {
            Toast.error(t('common.messages.unexpected_error'));
        }
    }, [t]);

    useEffect(() => {
        void loadCatalogs();
    }, []);

    const initMappingData = useCallback((data: ExcelSheetData, headerRow: number) => {
        const fams = familiesRef.current;
        const defaultElementTypeId = elementTypesRef.current.find(et => et.label.toLowerCase().includes('materiais'))?.value ?? null;
        const defaultUnitId = unitsRef.current.find(u => u.label.toLowerCase().includes('unidade'))?.value ?? null;
        const headerRowIdx = headerRow - 1;
        const headers = data.rows[headerRowIdx] ?? [];

        const mappings: ElementColumnMappingEntry[] = headers.map((cell, idx) => ({
            excelColumnIndex: idx,
            excelColumnName: cell,
            elementField: null,
        }));

        const dataRows = data.rows.slice(headerRow);
        const rawDataRows = data.rawRows?.slice(headerRow);
        const formulaDataRows = data.formulaRows?.slice(headerRow);
        const rows: ElementImportRow[] = dataRows.map((cells, idx) => {
            // Use raw values (exact precision) for data cells, formatted values for family matching
            const rawCells = rawDataRows?.[idx] ?? cells;
            const formulaCells = formulaDataRows?.[idx] ?? [];
            const cleanedCells = cells.map(c => c ? cleanElementName(c) : c);
            const hasData = cells.some(c => c?.trim());
            // Auto-match family using element name (column C by default, or first non-empty text)
            let familyId: string | null = null;
            let subFamilyId: string | null = null;

            if (hasData && fams.length > 0) {
                // Try to find element name from cells - use cell index 2 (column C) as default
                const nameCandidates = [cleanedCells[2], cleanedCells[1], cleanedCells[0]].filter(Boolean);
                for (const candidate of nameCandidates) {
                    if (candidate?.trim()) {
                        const match = matchFamily(candidate.trim(), fams);
                        if (match) {
                            familyId = match.familyId;
                            subFamilyId = match.subFamilyId;
                            break;
                        }
                    }
                }
            }

            // Auto-fill workTypeId from matched family
            let workTypeId: string | null = null;
            if (familyId) {
                const matchedFamily = fams.find(f => f.id === familyId);
                workTypeId = matchedFamily?.workTypeId ?? null;
            }

            return {
                rowIndex: idx,
                // dataRows = rows.slice(headerRow): first kept row is spreadsheet row (headerRow+1) 1-based.
                sheetRow: headerRow + 1 + idx,
                familyId,
                subFamilyId,
                elementTypeId: defaultElementTypeId,
                workTypeId,
                unitId: defaultUnitId,
                rawCells,
                formattedCells: cells,
                formulaCells,
            };
        });

        setColumnMappings(mappings);
        setImportRows(trimTrailingEmptyRows(rows));
    }, []);

    // Index of the Excel column mapped to family/subfamily name (-1 when not mapped)
    const familyNameColIndex = useMemo(
        () => columnMappings.find(m => m.elementField === 'familyName')?.excelColumnIndex ?? -1,
        [columnMappings]
    );
    const subFamilyNameColIndex = useMemo(
        () => columnMappings.find(m => m.elementField === 'subFamilyName')?.excelColumnIndex ?? -1,
        [columnMappings]
    );

    // When a family-name column is mapped, resolve each row's family/subfamily by that column's
    // text (exact, accent/case-insensitive). Rows without a match are left unmatched (warning).
    // When no family-name column is mapped, the automatic name-based match from initMappingData stands.
    useEffect(() => {
        if (familyNameColIndex < 0) return;
        const fams = familiesRef.current;
        setImportRows(prev => prev.map(row => {
            const familyValue = row.editedCells?.[familyNameColIndex] ?? row.rawCells[familyNameColIndex] ?? '';
            const subFamilyValue = subFamilyNameColIndex >= 0
                ? (row.editedCells?.[subFamilyNameColIndex] ?? row.rawCells[subFamilyNameColIndex] ?? '')
                : null;
            const match = matchFamilyByName(familyValue, subFamilyValue, fams);
            const familyId = match?.familyId ?? null;
            const subFamilyId = match?.subFamilyId ?? null;
            const workTypeId = familyId ? (fams.find(f => f.id === familyId)?.workTypeId ?? null) : null;
            return { ...row, familyId, subFamilyId, workTypeId };
        }));
    }, [familyNameColIndex, subFamilyNameColIndex, families]);

    const onFileLoaded = useCallback((data: ExcelSheetData, name: string) => {
        setExcelData(data);
        setFileName(name);
        initMappingData(data, headerRowNumber);
        setStep(1);

        // Restore saved mapping from database
        ElementsService.getImportSettings().then((settings: ImportSettingsDto) => {
            if (settings?.columnMappings) {
                try {
                    const savedMappings = JSON.parse(settings.columnMappings) as Record<string, string>;
                    const availableValues = new Set(mappingFieldsRef.current.map(f => f.value));

                    setColumnMappings(prev => {
                        const usedFields = new Set<string>();
                        return prev.map(m => {
                            const savedField = savedMappings[m.excelColumnName];
                            if (savedField && availableValues.has(savedField) && !usedFields.has(savedField)) {
                                usedFields.add(savedField);
                                return { ...m, elementField: savedField };
                            }
                            return m;
                        });
                    });
                } catch { /* ignore parse errors */ }
            }
        }).catch(() => { /* ignore - user just won't get saved mappings */ });
    }, [headerRowNumber, initMappingData]);

    const onHeaderRowChange = useCallback((row: number) => {
        setHeaderRowNumber(row);
        if (excelData) {
            initMappingData(excelData, row);
        }
    }, [excelData, initMappingData]);

    const onError = useCallback((message: string) => {
        Toast.error(message);
    }, []);

    const onRefreshFamilies = useCallback(() => {
        FamiliesService.getElementsCatalog().then(result => {
            setFamilies(result);
            familiesRef.current = result;
            Toast.success(t('elements.import_wizard.families_refreshed'));
        }).catch(() => {
            Toast.error(t('common.messages.unexpected_error'));
        });
    }, [t]);

    const onRefreshCatalogs = useCallback(async () => {
        try {
            const [elementTypesData, workTypesData, unitsData] = await Promise.all([
                ElementsService.getElementTypesCatalog(),
                WorkTypesService.getCatalog(),
                UnitsService.getFormattedCatalog(),
            ]);
            const elTypes = elementTypesData.map((x: ElementTypeDto) => ({ label: x.name, value: x.id }));
            const uList = unitsData.map((x: UnitDto) => ({ label: x.name, value: x.id }));
            setElementTypes(elTypes);
            elementTypesRef.current = elTypes;
            setWorkTypes(workTypesData);
            setUnits(uList);
            unitsRef.current = uList;
            Toast.success(t('elements.import_wizard.families_refreshed'));
        } catch {
            Toast.error(t('common.messages.unexpected_error'));
        }
    }, [t]);

    const onColumnMappingChange = useCallback((colIndex: number, field: string | null) => {
        setColumnMappings(prev => prev.map(m =>
            m.excelColumnIndex === colIndex
                ? { ...m, elementField: field }
                : m
        ));
    }, []);

    const onRowFamilyChange = useCallback((rowIndex: number, familyId: string | null) => {
        const family = familyId ? families.find(f => f.id === familyId) : null;
        const workTypeId = family?.workTypeId ?? null;
        setImportRows(prev => prev.map(r =>
            r.rowIndex === rowIndex ? { ...r, familyId, subFamilyId: null, workTypeId } : r
        ));
    }, [families]);

    const onRowSubFamilyChange = useCallback((rowIndex: number, subFamilyId: string | null) => {
        setImportRows(prev => prev.map(r =>
            r.rowIndex === rowIndex ? { ...r, subFamilyId } : r
        ));
    }, []);

    const onRowElementTypeChange = useCallback((rowIndex: number, elementTypeId: string | null) => {
        setImportRows(prev => prev.map(r =>
            r.rowIndex === rowIndex ? { ...r, elementTypeId } : r
        ));
    }, []);

    const onRowWorkTypeChange = useCallback((rowIndex: number, workTypeId: string | null) => {
        setImportRows(prev => prev.map(r =>
            r.rowIndex === rowIndex ? { ...r, workTypeId } : r
        ));
    }, []);

    const onCellEdit = useCallback((rowIndex: number, colIndex: number, value: string) => {
        setImportRows(prev => prev.map(r =>
            r.rowIndex === rowIndex
                ? { ...r, editedCells: { ...r.editedCells, [colIndex]: value } }
                : r
        ));
    }, []);

    // Polling logic
    const stopPolling = useCallback(() => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }
    }, []);

    const pollStatus = useCallback(async (id: string) => {
        try {
            const response = await ElementsService.getImportStatus(id);
            setStatus(response);

            if (response.status === 'completed' || response.status === 'failed') {
                stopPolling();

                if (response.status === 'completed') {
                    if (response.errors.length === 0) {
                        Toast.success(t('elements.import_wizard.import_success', { count: response.createdCount }));
                    } else {
                        Toast.warning(t('elements.import_wizard.import_partial', { created: response.createdCount, errors: response.errors.length }));
                    }
                } else {
                    Toast.error(t('elements.import_wizard.import_failed'));
                }
            }
        } catch (error) {
            console.error('Error polling status:', error);
        }
    }, [stopPolling, t]);

    const startPolling = useCallback((id: string) => {
        stopPolling();
        pollStatus(id);
        pollingRef.current = setInterval(() => pollStatus(id), 2000);
    }, [pollStatus, stopPolling]);

    useEffect(() => {
        return () => {
            stopPolling();
        };
    }, [stopPolling]);

    // Index of the column mapped to the sale price (-1 when unmapped).
    const priceColIndex = useMemo(
        () => columnMappings.find(m => m.elementField === 'price')?.excelColumnIndex ?? -1,
        [columnMappings]
    );
    const hasPriceColumn = priceColIndex >= 0;

    // A change to mappings/rows invalidates a previous price analysis.
    useEffect(() => {
        setPricePreviews(new Map());
        setPriceOverrides(new Map());
    }, [columnMappings, importRows]);

    const onPriceOverrideChange = useCallback((rowIndex: number, classification: PriceClassification | null) => {
        setPriceOverrides(prev => {
            const next = new Map(prev);
            if (classification) next.set(rowIndex, classification);
            else next.delete(rowIndex);
            return next;
        });
    }, []);

    const onAnalyzePrices = useCallback(async () => {
        if (!hasPriceColumn) return;
        try {
            setPriceAnalyzing(true);
            const request = buildPricePreviewRequest(importRows, columnMappings);
            const results = await ElementsService.previewImportPrices(request);
            const map = new Map<number, PriceFormulaPreview>();
            for (const r of results) {
                map.set(r.importRowIndex, {
                    rowIndex: r.importRowIndex,
                    classification: r.classification,
                    priceExpressionTemplate: r.priceExpressionTemplate,
                    displayFormula: r.displayFormula,
                });
            }
            setPricePreviews(map);
        } catch {
            Toast.error(t('common.messages.unexpected_error'));
        } finally {
            setPriceAnalyzing(false);
        }
    }, [hasPriceColumn, importRows, columnMappings, t]);

    // Importable rows (family + name) shown in the price step, with their computed price value.
    const importableRows: PricePreviewRowInfo[] = useMemo(() => {
        const nameColIndex = columnMappings.find(m => m.elementField === 'name')?.excelColumnIndex ?? -1;
        const referenceColIndex = columnMappings.find(m => m.elementField === 'reference')?.excelColumnIndex ?? -1;
        if (nameColIndex < 0) return [];
        return importRows
            .filter(r => r.familyId)
            .map(r => {
                const rawName = r.editedCells?.[nameColIndex] ?? r.rawCells[nameColIndex] ?? '';
                const name = cleanElementName(rawName);
                const reference = referenceColIndex >= 0
                    ? (r.editedCells?.[referenceColIndex] ?? r.rawCells[referenceColIndex] ?? '')
                    : '';
                const priceText = priceColIndex >= 0
                    ? cleanExcelNumber(r.editedCells?.[priceColIndex] ?? r.rawCells[priceColIndex] ?? '')
                    : '';
                return { rowIndex: r.rowIndex, reference, name, priceText };
            })
            .filter(r => r.name);
    }, [importRows, columnMappings, priceColIndex]);

    // Step 1 -> Step 2 (prices): analyse the price formulas, then advance to the review step.
    const handleGoToPrices = async () => {
        if (hasPriceColumn) {
            await onAnalyzePrices();
        }
        setStep(2);
    };

    // Step 2 (prices) -> Step 3 (import): run the actual import with the chosen decisions.
    const handleImport = async () => {
        const payload = buildImportPayload(importRows, columnMappings, pricePreviews, priceOverrides);

        if (payload.rows.length === 0) {
            Toast.warning(t('elements.import_wizard.no_rows_to_import'));
            return;
        }

        // Save mapping for next time
        const mapped = columnMappings.filter(m => m.elementField);
        if (mapped.length > 0) {
            const mappingsRecord = Object.fromEntries(
                mapped.map(m => [m.excelColumnName, m.elementField!])
            );
            ElementsService.saveImportSettings({
                columnMappings: JSON.stringify(mappingsRecord),
            }).catch(() => { /* ignore save errors */ });
        }

        try {
            setIsUploading(true);
            setStatus(null);
            setStep(3);

            const response = await ElementsService.startMappedImport(payload);
            setJobId(response.jobId);
            startPolling(response.jobId);
        } catch (error) {
            Toast.error(t('common.messages.unexpected_error'));
            setStep(2);
        } finally {
            setIsUploading(false);
        }
    };

    const canGoNext = excelData != null;
    const hasNameMapped = columnMappings.some(m => m.elementField === 'name');
    const isProcessing = status?.status === 'processing';
    const isCompleted = status?.status === 'completed';
    const isFailed = status?.status === 'failed';
    const progressPercentage = status && status.totalRows > 0
        ? Math.round((status.processedRows / status.totalRows) * 100)
        : 0;

    const handleReset = () => {
        setStep(0);
        setExcelData(null);
        setColumnMappings([]);
        setImportRows([]);
        setFileName('');
        setJobId(null);
        setStatus(null);
        setPricePreviews(new Map());
        setPriceOverrides(new Map());
    };

    return (
        <WizardFooterSlotContext.Provider value={footerSlot}>
        <ScreenTitle title={t('elements.import_wizard.title')}>
            <PageHeader
                title={t('elements.import_wizard.title')}
                informationText={t('elements.import_wizard.subtitle')}
                onGoBack={step > 0 ? () => setStep(s => Math.max(0, s - 1)) : undefined}
            />

            <PageContainer className={styles.pageContainer}>
                {/* Step indicators */}
                <div className={styles.steps}>
                    <div className={`${styles.step} ${step >= 0 ? styles.active : ''}`}>
                        <span className={styles.stepNumber}>1</span>
                        <span className={styles.stepLabel}>{t('elements.import_wizard.step_upload')}</span>
                    </div>
                    <div className={styles.stepDivider} />
                    <div className={`${styles.step} ${step >= 1 ? styles.active : ''}`}>
                        <span className={styles.stepNumber}>2</span>
                        <span className={styles.stepLabel}>{t('elements.import_wizard.step_mapping')}</span>
                    </div>
                    <div className={styles.stepDivider} />
                    <div className={`${styles.step} ${step >= 2 ? styles.active : ''}`}>
                        <span className={styles.stepNumber}>3</span>
                        <span className={styles.stepLabel}>{t('elements.import_wizard.step_prices')}</span>
                    </div>
                    <div className={styles.stepDivider} />
                    <div className={`${styles.step} ${step >= 3 ? styles.active : ''}`}>
                        <span className={styles.stepNumber}>4</span>
                        <span className={styles.stepLabel}>{t('elements.import_wizard.step_import')}</span>
                    </div>
                </div>

                <div className={styles.content}>
                    {/* Step 0: Upload */}
                    {step === 0 && (
                        <StepUpload
                            excelData={excelData}
                            fileName={fileName}
                            onFileLoaded={onFileLoaded}
                            onError={onError}
                        />
                    )}

                    {/* Step 1: Mapping */}
                    {step === 1 && excelData && (
                        <StepMapping
                            excelData={excelData}
                            headerRowNumber={headerRowNumber}
                            columnMappings={columnMappings}
                            importRows={importRows}
                            families={families}
                            mappingFields={mappingFields}
                            onColumnMappingChange={onColumnMappingChange}
                            onRowFamilyChange={onRowFamilyChange}
                            onRowSubFamilyChange={onRowSubFamilyChange}
                            onCellEdit={onCellEdit}
                            onHeaderRowChange={onHeaderRowChange}
                            onRefreshFamilies={onRefreshFamilies}
                            elementTypes={elementTypes}
                            workTypes={workTypes}
                            onRowElementTypeChange={onRowElementTypeChange}
                            onRowWorkTypeChange={onRowWorkTypeChange}
                            onRefreshCatalogs={onRefreshCatalogs}
                        />
                    )}

                    {/* Step 2: Price-formula analysis & overrides */}
                    {step === 2 && (
                        <StepPricePreview
                            rows={importableRows}
                            previews={pricePreviews}
                            overrides={priceOverrides}
                            hasPriceColumn={hasPriceColumn}
                            analyzing={priceAnalyzing}
                            onReanalyze={onAnalyzePrices}
                            onOverrideChange={onPriceOverrideChange}
                        />
                    )}

                    {/* Step 3: Import progress & results */}
                    {step === 3 && (
                        <>
                            {(isUploading || isProcessing) && (
                                <div className={styles.progressContainer}>
                                    <h5>{t('elements.import_wizard.processing')}</h5>
                                    {status && (
                                        <>
                                            <ProgressBar
                                                now={progressPercentage}
                                                label={`${progressPercentage}%`}
                                                animated
                                                striped
                                                style={{ marginBottom: '10px' }}
                                            />
                                            <p style={{ marginBottom: 0, fontSize: '14px', color: '#666' }}>
                                                {t('elements.import_wizard.rows_processed', { processed: status.processedRows, total: status.totalRows })}
                                                {status.createdCount > 0 && ` | ${t('elements.import_wizard.created_count', { count: status.createdCount })}`}
                                                {status.updatedCount > 0 && ` | ${t('elements.import_wizard.updated_count', { count: status.updatedCount })}`}
                                                {status.errorCount > 0 && ` | ${t('elements.import_wizard.error_count', { count: status.errorCount })}`}
                                            </p>
                                        </>
                                    )}
                                </div>
                            )}

                            {(isCompleted || isFailed) && status && (
                                <div className={`${styles.resultContainer} ${isFailed ? styles.failed : (status.errors.length > 0 ? styles.partial : styles.success)}`}>
                                    <h5>{isFailed ? t('elements.import_wizard.import_failed') : t('elements.import_wizard.import_result')}</h5>
                                    <p>
                                        <strong>{t('elements.import_wizard.elements_created')}:</strong> {status.createdCount}<br />
                                        <strong>{t('elements.import_wizard.elements_updated')}:</strong> {status.updatedCount}<br />
                                        <strong>{t('elements.import_wizard.suppliers_created')}:</strong> {status.suppliersCreated}
                                    </p>

                                    {status.errors.length > 0 && (
                                        <div style={{ color: isFailed ? '#721c24' : '#856404' }}>
                                            <strong>{t('elements.import_wizard.errors_label')} ({status.errors.length}):</strong>
                                            <ul style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                                {status.errors.map((err, i) => (
                                                    <li key={i}>{err}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    {status.createdElements.length > 0 && (
                                        <div>
                                            <strong>{t('elements.import_wizard.created_elements_list')}:</strong>
                                            <ul style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                                {status.createdElements.map((el) => (
                                                    <li key={el.id}>
                                                        {el.name} <span style={{ color: '#666', fontSize: '12px' }}>({el.familyName})</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    {status.updatedElements.length > 0 && (
                                        <div>
                                            <strong>{t('elements.import_wizard.updated_elements_list')}:</strong>
                                            <ul style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                                {status.updatedElements.map((el) => (
                                                    <li key={el.id}>
                                                        {el.name} <span style={{ color: '#666', fontSize: '12px' }}>({el.familyName})</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Navigation buttons (with table pagination portaled into the centre) */}
                <div className={styles.footer}>
                    <div className={styles.footerSide}>
                        {step === 1 && (
                            <Button
                                variant="secondary"
                                onClick={() => setStep(0)}
                            >
                                {t('elements.import_wizard.previous')}
                            </Button>
                        )}
                        {step === 2 && (
                            <Button
                                variant="secondary"
                                onClick={() => setStep(1)}
                                disabled={isUploading}
                            >
                                {t('elements.import_wizard.previous')}
                            </Button>
                        )}
                    </div>
                    <div ref={setFooterSlot} className={styles.footerCenter} />
                    <div className={`${styles.footerSide} ${styles.footerSideEnd}`}>
                        {step === 0 && (
                            <Button
                                onClick={() => setStep(1)}
                                disabled={!canGoNext}
                            >
                                {t('elements.import_wizard.next')}
                            </Button>
                        )}
                        {step === 1 && (
                            <Button
                                onClick={handleGoToPrices}
                                disabled={!hasNameMapped || priceAnalyzing}
                            >
                                {priceAnalyzing ? t('elements.import_wizard.price_analyzing') : t('elements.import_wizard.next')}
                            </Button>
                        )}
                        {step === 2 && (
                            <Button
                                onClick={handleImport}
                                disabled={isUploading}
                            >
                                {t('elements.import_wizard.confirm_import')}
                            </Button>
                        )}
                        {step === 3 && (isCompleted || isFailed) && (
                            <Button
                                onClick={handleReset}
                            >
                                {t('elements.import_wizard.new_import')}
                            </Button>
                        )}
                    </div>
                </div>
            </PageContainer>
        </ScreenTitle>
        </WizardFooterSlotContext.Provider>
    );
}

export default ImportElementsScreen;
